import React, { useState } from 'react';
import { CloseIcon } from '@/components/icons';
import { Button } from '@/components/ui';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  firstName: string;
  lastName: string;
  email: string;
  onUpdate: (data: { firstName: string; lastName: string }) => void;
  isUpdating?: boolean;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  firstName,
  lastName,
  email,
  onUpdate,
  isUpdating = false,
}) => {
  const [newFirstName, setNewFirstName] = useState(firstName);
  const [newLastName, setNewLastName] = useState(lastName);

  const isFormValid = newFirstName.trim() !== '' && newLastName.trim() !== '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    onUpdate({
      firstName: newFirstName.trim(),
      lastName: newLastName.trim(),
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-lg m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Edit My Profile</h2>
              <p className="text-sm text-gray-500 mt-1">Update your personal information.</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close modal">
              <CloseIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="edit-first-name" className="block text-sm font-semibold text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  id="edit-first-name"
                  placeholder="Enter First Name"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-wine-100 bg-[#FAFAFA]"
                />
              </div>
              <div>
                <label htmlFor="edit-last-name" className="block text-sm font-semibold text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  id="edit-last-name"
                  placeholder="Enter Last Name"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-wine-100 bg-[#FAFAFA]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={!isFormValid || isUpdating}
              className="w-full"
            >
              {isUpdating ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
